import Honeybadger from 'honeybadger-js';
import React, { useMemo } from 'react';

import { useSendRsvpReminderEmailMutation } from '../../../generated/graphql';
import { getChangedValues } from '../../../helpers/getChangedValues';
import { splitHouseholds } from '../../../helpers/households';
import { editRsvpDeadlineReminderEmailPath, editRsvpDeadlineReminderEmailRoute } from '../../../helpers/routes';
import EmailSequence from '../../SharePage/EmailSequence';
import { emailSharingSchema } from '../../SharePage/sharingValidation';
import useShareTracking from '../../SharePage/useShareTracking';
import { EmailPreviewContainer } from '../EmailPreviewContainer';
import { RSVP_STATUS } from '../GuestFilters';
import { useFilteredHouseholds } from '../GuestFilters/useFilteredHouseholds';
import EmailEditorRail from './ShareWebsiteEmail/EmailEditorRail';
import { useSequenceState } from './useSequenceState';

export interface ShareValues {
  subject: string;
  message: string;
  linkButtonText: string;
}

const RsvpDeadlineReminderEmail: React.FC = () => {
  const [sendEmail, { loading: sendingMessage }] = useSendRsvpReminderEmailMutation();

  const {
    sequence,
    wwsLoading,
    guestIds,
    setGuestIds,
    setHideRecaptcha,
    hideRecaptcha,
    dispatch,
    recaptchaRef,
    seqId,
    showError,
    errorMessage,
    showSuccess,
    householdLoading,
  } = useSequenceState();

  const { filteredHouseholds, setFilterCriteria } = useFilteredHouseholds();
  const households = useMemo(() => splitHouseholds('email', filteredHouseholds), [filteredHouseholds]);

  const loading = wwsLoading || sendingMessage;
  const track = useShareTracking();

  const message = sequence?.messagingSequence?.defaultEmailBody ?? '';

  let initialValues: ShareValues = {
    subject: sequence?.messagingSequence?.defaultEmailSubject ?? '',
    message,
    linkButtonText: sequence?.messagingSequence?.defaultLinkText ?? '',
  };

  const sendMessage = async (values: ShareValues) => {
    if (loading) {
      return;
    }

    const edited = getChangedValues(initialValues, values);

    track('Website Shared', {
      guestMessagedCount: guestIds.length,
      message: values.message,
      messageCustomized: Boolean(Object.keys(edited).length),
      messageType: sequence?.messagingSequence?.displayName,
      medium: 'email',
    });

    dispatch({ type: 'RESET', payload: { hideRecaptcha: true, showSuccess: false, showError: false } });

    try {
      // Convert all newlines in the message body into <br /> tags.
      const convertedMessage = values.message.replace(/\n/g, '<br/>');
      await sendEmail({
        variables: {
          args: {
            subject: values.subject,
            body: convertedMessage,
            linkButtonText: values.linkButtonText,
            guestIds,
            token: recaptchaRef.current?.getValue() || '',
          },
        },
      });
      dispatch({ type: 'SUCCESS' });
    } catch (error) {
      Honeybadger.notify(error, 'Error sending RSVP reminder email', { context: { error } });
      dispatch({ type: 'ERROR', payload: { message: error.message } });
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  const editRoute = editRsvpDeadlineReminderEmailRoute(seqId);

  const filterPreset = { rsvpStatus: RSVP_STATUS.NO_RESPONSE, event: null };

  return (
    <EmailSequence
      loading={loading}
      showError={showError}
      errorMessage={errorMessage}
      showSuccess={showSuccess}
      initialValues={initialValues}
      onSend={sendMessage}
      PreviewComponent={
        <EmailPreviewContainer
          editRoute={editRoute}
          matchPath={editRsvpDeadlineReminderEmailPath}
          displayName={sequence?.messagingSequence?.displayName}
        />
      }
      EditorComponent={
        <EmailEditorRail
          matchPath={editRsvpDeadlineReminderEmailPath}
          displayName={sequence?.messagingSequence?.displayName}
          initialValues={initialValues}
        />
      }
      hideRecaptcha={hideRecaptcha}
      setHideRecaptcha={setHideRecaptcha}
      recaptchaRef={recaptchaRef}
      setGuestIds={setGuestIds}
      editPath={editRsvpDeadlineReminderEmailPath}
      editRoute={editRoute}
      validationSchema={emailSharingSchema}
      households={households}
      householdLoading={householdLoading}
      setFilter={setFilterCriteria}
      filterPreset={filterPreset}
      displayName={sequence?.messagingSequence?.displayName}
    />
  );
};

export default RsvpDeadlineReminderEmail;
